// A project's apps, each with its own versions.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "./client";
import type { App } from "./types/App";
import type { AppPlatform } from "./types/AppPlatform";
import type { CreateAppRequest } from "./types/CreateAppRequest";
import type { IgnoredTag } from "./types/IgnoredTag";
import type { MergeAppRequest } from "./types/MergeAppRequest";
import type { UpdateAppRequest } from "./types/UpdateAppRequest";
import type { VersionSource } from "./types/VersionSource";

const enc = encodeURIComponent;
const appsPath = (key: string) => `/projects/${enc(key)}/apps`;

/** The server applies the same defaults when a request leaves these out. */
export function defaultSource(platforms: AppPlatform[]): VersionSource {
  return platforms.length > 0 && platforms.every((p) => p === "web" || p === "backend") ? "deployment" : "manual";
}

export function defaultTagMeansReleased(platforms: AppPlatform[]): boolean {
  return !platforms.includes("android") && !platforms.includes("ios");
}

/** Without `includeArchived`, the apps a picker should offer. */
export function useApps(projectKey: string, { includeArchived = false } = {}) {
  return useQuery({
    queryKey: ["apps", projectKey, includeArchived],
    queryFn: () => get<App[]>(`${appsPath(projectKey)}${includeArchived ? "?archived=true" : ""}`),
  });
}

export function useIgnoredTags(projectKey: string, appId: string) {
  return useQuery({
    queryKey: ["apps", projectKey, "ignored-tags", appId],
    queryFn: () => get<IgnoredTag[]>(`${appsPath(projectKey)}/${enc(appId)}/ignored-tags`),
  });
}

export function useAppMutations(projectKey: string) {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ["apps", projectKey] });
  return {
    create: useMutation({ mutationFn: (body: CreateAppRequest) => post<App>(appsPath(projectKey), body), onSuccess: done }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: UpdateAppRequest }) => patch<App>(`${appsPath(projectKey)}/${enc(id)}`, body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => del(`${appsPath(projectKey)}/${enc(id)}`), onSuccess: done }),
    merge: useMutation({
      mutationFn: ({ id, into }: { id: string; into: string }) => post<App>(`${appsPath(projectKey)}/${enc(id)}/merge`, { into } satisfies MergeAppRequest),
      onSuccess: () => {
        done();
        void qc.invalidateQueries({ queryKey: ["releases", projectKey] });
      },
    }),
  };
}
